import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AdminSettingsKeys,
  getDefaultSubscriptionPrice,
  getForceOneToMany,
  getSubscriptionPrices,
  setDefaultSubscriptionPrice,
  setForceOneToMany,
} from "@/features/admins/api/admin-settings";
import { useToast } from "@/hooks/use-toast";
import { StripePrice } from "@/types/stripe";

const priceFormSchema = z.object({
  priceId: z.string().min(1, "Please select a subscription price"),
});

type PriceFormValues = z.infer<typeof priceFormSchema>;

const formatPrice = (price: StripePrice): string => {
  const amount = price.unit_amount / 100;
  const currency = price.currency.toUpperCase();
  const interval = price.recurring?.interval || "one-time";
  const intervalCount = price.recurring?.interval_count || 1;

  const intervalText =
    intervalCount > 1 ? `${intervalCount} ${interval}s` : interval;

  return `${currency} ${amount.toFixed(2)} / ${intervalText}`;
};

const AdminSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch subscription prices
  const {
    data: prices = [],
    isLoading: pricesLoading,
    error: pricesError,
  } = useQuery<StripePrice[]>({
    queryKey: [AdminSettingsKeys.GET_SUBSCRIPTION_PRICES],
    queryFn: getSubscriptionPrices,
  });

  // Fetch current default price
  const { data: defaultPrice, isLoading: defaultPriceLoading } = useQuery({
    queryKey: [AdminSettingsKeys.GET_DEFAULT_SUBSCRIPTION_PRICE],
    queryFn: getDefaultSubscriptionPrice,
  });

  // Fetch force one-to-many setting
  const { data: forceOneToManyData, isLoading: forceOneToManyLoading } =
    useQuery({
      queryKey: [AdminSettingsKeys.GET_FORCE_ONE_TO_MANY],
      queryFn: getForceOneToMany,
    });

  const priceForm = useForm<PriceFormValues>({
    resolver: zodResolver(priceFormSchema),
    defaultValues: {
      priceId: "",
    },
  });

  // Update price form when default price is loaded
  React.useEffect(() => {
    if (defaultPrice?.priceId) {
      priceForm.setValue("priceId", defaultPrice.priceId);
    }
  }, [defaultPrice, priceForm]);

  // Set default subscription price mutation
  const { mutate: setPrice, isPending: isPricePending } = useMutation({
    mutationFn: setDefaultSubscriptionPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [AdminSettingsKeys.GET_DEFAULT_SUBSCRIPTION_PRICE],
      });
      toast({
        title: "Success",
        description: "Default subscription price updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to update default subscription price",
        variant: "destructive",
      });
    },
  });

  // Set force one-to-many mutation
  const { mutate: toggleForceOneToMany, isPending: isForceOneToManyPending } =
    useMutation({
      mutationFn: setForceOneToMany,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [AdminSettingsKeys.GET_FORCE_ONE_TO_MANY],
        });
        toast({
          title: "Success",
          description: "Force 1:many setting updated successfully",
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description:
            error.message || "Failed to update force 1:many setting",
          variant: "destructive",
        });
      },
    });

  const onPriceSubmit = (values: PriceFormValues) => {
    setPrice(values.priceId);
  };

  const isPricesLoading = pricesLoading || defaultPriceLoading;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage default subscription pricing and other admin configurations
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Default Subscription Price</CardTitle>
            <CardDescription>
              Select the default subscription price that will be used for new
              subscriptions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isPricesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : pricesError ? (
              <div className="text-destructive py-4">
                Failed to load subscription prices. Please try again.
              </div>
            ) : (
              <Form {...priceForm}>
                <form
                  onSubmit={priceForm.handleSubmit(onPriceSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={priceForm.control}
                    name="priceId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subscription Price</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={isPricePending}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a subscription price" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {prices.length === 0 ? (
                              <SelectItem value="empty" disabled>
                                No subscription prices available
                              </SelectItem>
                            ) : (
                              prices
                                .filter((price) => price.active)
                                .map((price) => (
                                  <SelectItem key={price.id} value={price.id}>
                                    <div className="flex flex-col gap-0.5">
                                      <span className="font-medium">
                                        {formatPrice(price)}
                                      </span>
                                      {price.nickname && (
                                        <span className="text-sm text-muted-foreground">
                                          {price.nickname}
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))
                            )}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          This price will be used as the default for new
                          subscriptions
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={isPricePending || !priceForm.formState.isDirty}
                    >
                      {isPricePending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Force All Meetings to 1:Many</CardTitle>
            <CardDescription>
              When enabled, all applicants will be routed to 1:many group
              meetings regardless of their qualified lead status.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {forceOneToManyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="force-one-to-many">
                    Force 1:many meetings
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {forceOneToManyData?.forceOneToMany
                      ? "All meetings are currently forced to 1:many"
                      : "Meetings are routed based on qualified lead status"}
                  </p>
                </div>
                <Switch
                  id="force-one-to-many"
                  checked={forceOneToManyData?.forceOneToMany ?? false}
                  onCheckedChange={(checked) => toggleForceOneToMany(checked)}
                  disabled={isForceOneToManyPending}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminSettings;
